import { isBrowser } from '../helpers/isBrowser';
import { Consent } from './useCookieConsentStore.types';

export const GTM_STORE_VERSION = 1;

export const CONSENT_COOKIE_NAME = 'beam_cookie_consent';

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

/**
 * Generates a consent expiration date set to one year from the current date.
 *
 * @returns {Date} The expiration date one year from now.
 */
export const getConsentExpirationDate = () => {
  const date = new Date();
  date.setFullYear(date.getFullYear() + 1);
  return date;
};

/**
 * Sends a Google Tag Manager (GTM) event to the dataLayer.
 *
 * @template T - The type of the event object, which must include an `event` property.
 * @param {T} event - The event object to be sent to the GTM dataLayer.
 * @returns {void}
 */
export const sendGTMEvent = <T extends { event: string }>(event: T) => {
  if (!isBrowser) return;
  window.dataLayer?.push(event);
};

/**
 * Sends a Google Tag Manager (GTM) consent update based on the provided consent object.
 *
 * @param consent - An object representing the user's consent preferences. Each key-value pair
 *                  in the object corresponds to a specific consent type and its status.
 *                  A value of `true` indicates consent is granted, while `false` indicates consent is denied.
 *
 * @example
 * ```typescript
 * const consent = {
 *   ad_personalization: true,
 *   ad_storage: false,
 *   ad_user_data: true,
 *   analytics_storage: false,
 *   functionality_storage: true,
 * };
 * sendGTMConsentUpdate(consent);
 * ```
 */
export const sendGTMConsentUpdate = (consent: Consent) => {
  if (!isBrowser) return;
  window.gtag?.('consent', 'update', convertGTMConsentValues(consent));
};

/**
 * Converts a Consent object to a new object with the same keys,
 * but with values converted to 'granted' or 'denied' strings.
 *
 * @param consent - The Consent object to be converted.
 * @returns A new object with the same keys as the input Consent object,
 *          but with values as 'granted' or 'denied' based on the boolean value.
 */
export const convertGTMConsentValues = (consent: Consent) =>
  Object.fromEntries(
    Object.entries(consent).map(([key, value]) => [
      key,
      value ? 'granted' : 'denied',
    ]),
  );
